import {
  CheckIcon,
  CodeIcon,
  GlobeIcon,
  PencilIcon,
  UploadIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useEffect, useRef, useState } from 'react'
import { api, type Track, type User } from './api.ts'
import { useAuth } from './AuthContext.tsx'
import { resizeImageToJpeg } from './image-utils.ts'
import { NavBar } from './NavBar.tsx'
import { PaginatedTrackList } from './PaginatedTrackList.tsx'
import { ProfileImage } from './ProfileImage.tsx'
import { navigate } from './Router.tsx'
import { useNavBarHeightStore } from './store.ts'
import { useTrackList } from './useTrackList.ts'

interface ProfileProps {
  userId: string
}

export function Profile({ userId }: ProfileProps) {
  const { user: currentUser, refreshUser } = useAuth()
  const [profileUser, setProfileUser] = useState<User | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [cursor, setCursor] = useState<string | undefined>()
  const [hasMore, setHasMore] = useState(true)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [saveError, setSaveError] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [profileImageKey, setProfileImageKey] = useState(0)
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { height: navBarHeight } = useNavBarHeightStore()

  const trackList = useTrackList({
    pageType: 'profile',
    tracks,
    loading,
    onTracksUpdate: setTracks,
  })

  const isOwnProfile = currentUser?.id === userId

  useEffect(() => {
    loadUserProfile()
  }, [userId])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsVerySmallScreen(window.innerWidth < 700)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    return () => window.removeEventListener('resize', checkScreenSize)
  }, [])

  useEffect(() => {
    if (tracks.length > 0) {
      loadUserProfile()
    }
  }, [currentUser])

  useEffect(() => {
    if (profileUser?.name) {
      document.title = `${profileUser.name} - loopmaster`
    }
  }, [profileUser])

  const loadUserProfile = async (append = false) => {
    try {
      if (!append) {
        setLoading(true)
      }

      const [userResponse, tracksResponse] = await Promise.all([
        append ? Promise.resolve({ user: profileUser! }) : api.users.getUser(userId),
        api.users.getUserTracks(userId, 5, append ? cursor : undefined),
      ])

      if (!append) {
        setProfileUser(userResponse.user)
        setEditedName(userResponse.user.name)
      }

      if (append) {
        setTracks(prev => [...prev, ...tracksResponse.tracks])
      }
      else {
        setTracks(tracksResponse.tracks)
      }

      setCursor(tracksResponse.cursor)
      setHasMore(!!tracksResponse.cursor)

      trackList.loadTrackData(tracksResponse.tracks, append)
    }
    catch (err: any) {
      setError(err.message)
    }
    finally {
      if (!append) {
        setLoading(false)
      }
    }
  }

  const loadMore = async () => {
    await loadUserProfile(true)
  }

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      setSaveError('Name cannot be empty')
      return
    }

    try {
      setSaveError('')
      const response = await api.users.updateUser(userId, { name: editedName.trim() })
      setProfileUser(response.user)
      setIsEditingName(false)
      // Reload tracks to update userName in all track entries
      await loadUserProfile()
    }
    catch (err: any) {
      setSaveError(err.message)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setSaveError('Please select an image file')
      return
    }

    try {
      setUploadingImage(true)
      setSaveError('')

      const resizedBlob = await resizeImageToJpeg(file, 80)
      await api.users.uploadProfileImage(userId, resizedBlob)

      setProfileImageKey(prev => prev + 1)
    }
    catch (err: any) {
      setSaveError(err.message || 'Failed to upload image')
    }
    finally {
      setUploadingImage(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="w-[100dvw] h-[100dvh] overflow-hidden text-[#f0f0f0] flex flex-col">
      <NavBar
        onPlay={trackList.handleNavBarPlay}
        onStop={trackList.handleNavBarStop}
        rightSideButtons={
          <>
            <button
              onClick={() => {
                navigate('/')
              }}
              className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-lime-400 to-green-600 text-white hover:opacity-90 flex items-center gap-2"
            >
              <GlobeIcon weight="regular" size={16} />
              <span className="hidden md:inline">Browse</span>
            </button>
            {!isVerySmallScreen && (
              <button
                onClick={() => {
                  navigate('/editor')
                }}
                className="px-4 h-8 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-orange-500 to-red-600 text-white hover:opacity-90 flex items-center gap-2"
              >
                <CodeIcon weight="regular" size={16} />
                Switch to Editor
              </button>
            )}
          </>
        }
      />

      <div className="relative" style={{ height: `calc(100dvh - ${navBarHeight}px)` }}>
        <div className="relative h-full overflow-y-auto overflow-x-hidden p-6 pointer-events-none">
          <div className="pointer-events-auto inline-block w-full">
            <PaginatedTrackList
              tracks={tracks}
              loading={loading}
              header={profileUser
                ? (
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="w-20 h-20">
                        <ProfileImage
                          userId={userId}
                          userName={profileUser.name}
                          size={80}
                          cacheKey={profileImageKey}
                          className="w-20 h-20"
                          hasAvatar={profileUser.hasAvatar}
                        />
                      </div>
                      {isOwnProfile && (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="absolute bottom-0 right-0 p-1.5 bg-[#10b981] text-white rounded-full hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Upload profile image"
                          >
                            <UploadIcon size={14} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                    <div className="flex-1">
                      {isEditingName
                        ? (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={editedName}
                              onChange={e => setEditedName(e.target.value)}
                              className="px-3 py-2 bg-[#1a1a1a] border border-[#444] rounded text-lg font-semibold"
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  handleSaveName()
                                }
                                else if (e.key === 'Escape') {
                                  setIsEditingName(false)
                                  setEditedName(profileUser.name)
                                  setSaveError('')
                                }
                              }}
                              autoFocus
                            />
                            <button
                              onClick={handleSaveName}
                              className="p-2 bg-[#10b981] text-white rounded hover:bg-[#059669]"
                              title="Save"
                            >
                              <CheckIcon size={20} weight="bold" />
                            </button>
                            <button
                              onClick={() => {
                                setIsEditingName(false)
                                setEditedName(profileUser.name)
                                setSaveError('')
                              }}
                              className="p-2 bg-[#444] text-white rounded hover:bg-[#555]"
                              title="Cancel"
                            >
                              <XIcon size={20} weight="bold" />
                            </button>
                          </div>
                        )
                        : (
                          <div className="flex items-center gap-2">
                            <h2 className="text-4xl font-extrabold font-[Turret_Road] text-lime-400">
                              {profileUser.name}
                            </h2>
                            {isOwnProfile && (
                              <button
                                onClick={() => setIsEditingName(true)}
                                className="p-1 text-[#888] hover:text-white"
                                title="Edit name"
                              >
                                <PencilIcon size={18} />
                              </button>
                            )}
                          </div>
                        )}
                      {saveError && <div className="text-red-500 text-sm mt-1">{saveError}</div>}
                      <div className="text-sm text-[#888]">
                        {profileUser.tracksCount ?? tracks.length}{' '}
                        loop{(profileUser.tracksCount ?? tracks.length) !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                )
                : (
                  null
                )}
              activeTrackId={trackList.activeTrackId}
              actuallyPlaying={trackList.actuallyPlaying}
              trackParents={trackList.trackParents}
              trackRemixes={trackList.trackRemixes}
              likedTrackIds={trackList.likedTrackIds}
              activeRemixId={trackList.activeRemixId}
              compileMode={trackList.compileMode}
              hasMore={hasMore}
              loadMore={loadMore}
              onSetActiveTrackId={trackList.setActiveTrackId}
              onSetActiveRemixId={trackList.setActiveRemixId}
              onSetActuallyPlaying={trackList.setActuallyPlaying}
              onLoad={trackList.handleLoad}
              onLoadInA={trackList.handleLoadInA}
              onLoadInB={trackList.handleLoadInB}
              onToggleLike={trackList.handleToggleLike}
              onPlayRemix={trackList.handlePlayRemix}
              onStopRemix={trackList.handleStopRemix}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
